// Custom Block Library — gate validators (pure logic; execution injected via BlockGateRuntime).

import type { BlockSubmission, GateResult, ParameterSchema } from '../../types/customBlockLibrary';
import { type BlockGateRuntime, type GateCandle, entryPointFor, isRunError, referencedParams, syntheticCandles } from './runtime';

const isFiniteNum = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);

// ── Gate: metadata + parameter schema ────────────────────────────────────────
export function metadataGate(sub: BlockSubmission): GateResult {
  if (!sub.name?.trim() || !sub.description?.trim()) return { gate: 'metadata', passed: false, detail: 'name + description required' };
  const entry = entryPointFor(sub.blockType);
  if (!new RegExp(`function\\s+${entry}\\b`).test(sub.code!)) return { gate: 'metadata', passed: false, detail: `code must define ${entry}() for blockType '${sub.blockType}'` };
  const schema: ParameterSchema = sub.paramSchema ?? {};
  for (const p of referencedParams(sub.code!)) {
    const spec = schema[p];
    if (!spec) return { gate: 'metadata', passed: false, detail: `param '${p}' used in code has no schema entry` };
    if (spec.default === undefined) return { gate: 'metadata', passed: false, detail: `param '${p}' has no default` };
    if ((spec.kind === 'int' || spec.kind === 'float')) {
      if (!isFiniteNum(spec.min) || !isFiniteNum(spec.max) || (spec.min as number) >= (spec.max as number)) return { gate: 'metadata', passed: false, detail: `numeric param '${p}' needs min < max` };
    }
    if (spec.kind === 'enum' && (!Array.isArray(spec.values) || spec.values.length === 0)) return { gate: 'metadata', passed: false, detail: `enum param '${p}' needs values[]` };
  }
  return { gate: 'metadata', passed: true };
}

// ── Gate: determinism (per-bar blocks) ───────────────────────────────────────
export async function determinismGate(sub: BlockSubmission, rt: BlockGateRuntime, seeds: number[] = [0, 1, 2]): Promise<GateResult> {
  const params = defaults(sub.paramSchema);
  for (const seed of seeds) {
    const candles = syntheticCandles(700, seed);
    const a = await rt.runPerBar(sub.code!, sub.blockType, candles, params);
    if (isRunError(a)) return { gate: 'determinism', passed: false, detail: `threw on normal data: ${a.error}` };
    const b = await rt.runPerBar(sub.code!, sub.blockType, candles, params);
    if (isRunError(b)) return { gate: 'determinism', passed: false, detail: 'non-deterministic (errored on rerun)' };
    if (a.value.length !== candles.length) return { gate: 'determinism', passed: false, detail: `output length ${a.value.length} != candles ${candles.length}` };
    for (let i = 0; i < a.value.length; i++) {
      const x = a.value[i], y = b.value[i];
      if (!(x === y || (Number.isNaN(x) && Number.isNaN(y)))) return { gate: 'determinism', passed: false, detail: `same input → different output at bar ${i} (seed ${seed})` };
      if (x === Infinity || x === -Infinity) return { gate: 'determinism', passed: false, detail: `Infinity in output at bar ${i}` };
    }
    // No-lookahead / prefix-invariance: value[i] must not change when FUTURE bars are appended.
    const k = Math.floor(candles.length * 0.6);
    const pref = await rt.runPerBar(sub.code!, sub.blockType, candles.slice(0, k), params);
    if (isRunError(pref)) return { gate: 'no_lookahead', passed: false, detail: `threw on prefix (seed ${seed})` };
    for (let i = 0; i < k; i++) {
      const x = pref.value[i], y = a.value[i];
      if (!(x === y || (Number.isNaN(x) && Number.isNaN(y)))) return { gate: 'no_lookahead', passed: false, detail: `bar ${i} changed when future bars appended (lookahead)` };
    }
  }
  return { gate: 'determinism', passed: true };
}

// ── Gate: compatibility (executes + round-trips) ─────────────────────────────
export async function compatibilityGate(sub: BlockSubmission, rt: BlockGateRuntime): Promise<GateResult> {
  const params = defaults(sub.paramSchema);
  // round-trip the serialized block payload
  const payload = { code: sub.code!, parameters: params, blockType: sub.blockType };
  const rt2 = JSON.parse(JSON.stringify(payload));
  if (rt2.code !== sub.code!) return { gate: 'compatibility', passed: false, detail: 'code not round-trip stable' };
  if (rt.backtest) {
    const candles = syntheticCandles(800, 0);
    const r = await rt.backtest(sub.code!, sub.blockType, candles, params);
    if (isRunError(r)) return { gate: 'compatibility', passed: false, detail: `backtest threw: ${r.error}` };
  }
  return { gate: 'compatibility', passed: true };
}

// ── Category: sizing output bounds ───────────────────────────────────────────
export async function sizingBoundsGate(sub: BlockSubmission, rt: BlockGateRuntime): Promise<GateResult> {
  if (!rt.runQty) return { gate: 'sizing_bounds', passed: false, detail: 'runtime cannot run sizing blocks' };
  const params = defaults(sub.paramSchema);
  const ctxs = [
    { equity: 100000, dayStartEquity: 100000, initialCapital: 100000, entryPrice: 100, stopDistance: 2, atr: 1.5, direction: 'long' },
    { equity: 5000, dayStartEquity: 6000, initialCapital: 10000, entryPrice: 30000, stopDistance: 150, atr: 80, direction: 'short' },
    { equity: 250000, dayStartEquity: 250000, initialCapital: 250000, entryPrice: 1.05, stopDistance: 0.002, atr: 0.001, direction: 'long' },
  ];
  for (const ctx of ctxs) {
    const q1 = await rt.runQty(sub.code!, ctx, params); const q2 = await rt.runQty(sub.code!, ctx, params);
    if (isRunError(q1)) return { gate: 'sizing_bounds', passed: false, detail: `sizing threw: ${q1.error}` };
    if (!isFiniteNum(q1)) return { gate: 'sizing_bounds', passed: false, detail: `qty not finite (got ${q1})` };
    if (q1 < 0) return { gate: 'sizing_bounds', passed: false, detail: `negative qty (${q1})` };
    if (isRunError(q2) || q1 !== q2) return { gate: 'sizing_bounds', passed: false, detail: 'non-deterministic qty' };
    // sanity cap: notional must not exceed a generous multiple of equity (no absurd leverage / overflow)
    if (q1 * ctx.entryPrice > ctx.equity * 50) return { gate: 'sizing_bounds', passed: false, detail: `qty implies >50x notional vs equity` };
  }
  return { gate: 'sizing_bounds', passed: true };
}

// ── Category: risk_rule lifecycle + no-lookahead / prefix-invariance ──────────
export async function riskLifecycleGate(sub: BlockSubmission, rt: BlockGateRuntime): Promise<GateResult> {
  if (!rt.runRiskRuleCheckBar) return { gate: 'risk_lifecycle', passed: false, detail: 'runtime cannot run risk_rule blocks' };
  const params = defaults(sub.paramSchema);
  const full: GateCandle[] = syntheticCandles(600, 0);
  const fullA = await rt.runRiskRuleCheckBar(sub.code!, full, params);
  if (isRunError(fullA)) return { gate: 'risk_lifecycle', passed: false, detail: `risk_rule threw: ${fullA.error}` };
  // prefix-invariance: decisions on bars ≤ k must be identical whether or not future bars exist.
  for (const k of [150, 300, 450]) {
    const pref = await rt.runRiskRuleCheckBar(sub.code!, full.slice(0, k), params);
    if (isRunError(pref)) return { gate: 'risk_lifecycle', passed: false, detail: `risk_rule threw on prefix ${k}` };
    for (let i = 0; i < k; i++) {
      const a = JSON.stringify(pref[i] ?? null), b = JSON.stringify(fullA[i] ?? null);
      if (a !== b) return { gate: 'no_lookahead', passed: false, detail: `decision at bar ${i} changed when future bars added (lookahead)` };
    }
  }
  return { gate: 'risk_lifecycle', passed: true };
}

// ── Category: stricter risk_rule OPTIMIZER gate (Phase 4) ─────────────────────
// A risk_rule may enter the optimizer mutation pool only if it clears this stricter bar (beyond the
// publish-time riskLifecycleGate): deterministic across seeds, no-lookahead/prefix-invariance, ONLY valid
// bounded actions, and bounded output (one decision per bar). Resource/timeout limits are enforced by the
// runtime isolate (a hang → RunError → fail). Parameter-boundedness is checked separately in runGate.
const VALID_RISK_ACTIONS = new Set(['block_entry', 'resize_qty', 'allow', 'flatten']);
export async function riskOptimizerGate(sub: BlockSubmission, rt: BlockGateRuntime): Promise<GateResult> {
  if (!rt.runRiskRuleCheckBar) return { gate: 'risk_lifecycle', passed: false, detail: 'runtime cannot run risk_rule blocks' };
  const params = defaults(sub.paramSchema);
  for (const seed of [0, 1, 2]) {
    const candles = syntheticCandles(500, seed);
    const a = await rt.runRiskRuleCheckBar(sub.code!, candles, params);
    if (isRunError(a)) return { gate: 'risk_lifecycle', passed: false, detail: `risk_rule threw/timed out: ${a.error}` };
    const b = await rt.runRiskRuleCheckBar(sub.code!, candles, params);
    if (isRunError(b) || JSON.stringify(a) !== JSON.stringify(b)) return { gate: 'determinism', passed: false, detail: 'non-deterministic risk_rule actions' };
    if (a.length !== candles.length) return { gate: 'risk_lifecycle', passed: false, detail: `actions length ${a.length} != bars ${candles.length}` };
    for (let i = 0; i < a.length; i++) {
      const act = a[i];
      if (act === null) continue;
      if (typeof act !== 'object' || typeof (act as { action?: unknown }).action !== 'string' || !VALID_RISK_ACTIONS.has((act as { action: string }).action)) {
        return { gate: 'risk_lifecycle', passed: false, detail: `invalid action at bar ${i}: ${JSON.stringify(act)} (allowed: ${[...VALID_RISK_ACTIONS].join('/')}/null)` };
      }
      if (JSON.stringify(act).length > 200) return { gate: 'risk_lifecycle', passed: false, detail: `unbounded action payload at bar ${i}` };
    }
  }
  // no-lookahead / prefix-invariance on the decisions
  const full = syntheticCandles(500, 0);
  const fullA = await rt.runRiskRuleCheckBar(sub.code!, full, params);
  if (isRunError(fullA)) return { gate: 'risk_lifecycle', passed: false, detail: 'risk_rule threw on full series' };
  for (const k of [150, 300]) {
    const pref = await rt.runRiskRuleCheckBar(sub.code!, full.slice(0, k), params);
    if (isRunError(pref)) return { gate: 'no_lookahead', passed: false, detail: `risk_rule threw on prefix ${k}` };
    for (let i = 0; i < k; i++) if (JSON.stringify(pref[i] ?? null) !== JSON.stringify(fullA[i] ?? null)) return { gate: 'no_lookahead', passed: false, detail: `decision at bar ${i} changed with future bars (lookahead)` };
  }
  return { gate: 'risk_lifecycle', passed: true };
}

export function defaults(schema: ParameterSchema | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(schema ?? {})) out[k] = v.default;
  return out;
}
